/**
 * `src/lib/content/quote.ts` — Layer 3 of the anchor, sourced from the corpus.
 * SPEC §3.1 (`quote`, `text_hash_raw`, `text_hash_fold`), §3.2 (the ladder's
 * re-derived span).
 *
 * The remarks system at M3 needs four things at the moment a user writes a note:
 * the `ref`, the verbatim span, the ≤200-character `quote`, and the two hashes.
 * This file produces all four from an `AyahModel` — and produces NONE of them
 * itself. `makeQuote`, `spanText`, `fold` and `anchorHashes` all come from the
 * frozen module; a second implementation of any of them is a second thing that
 * can disagree with the first, and `text_hash_fold` is stored on every note
 * forever.
 *
 * THE MINT/RE-DERIVE SPLIT, WHICH IS THE FROZEN MODULE'S OWN SPLIT
 * ───────────────────────────────────────────────────────────────
 * Minting HARD REJECTS a word range that runs past the ayah — `wordsRef()`
 * checks against the real count, because a coordinate this app creates must
 * name a place that exists. Re-deriving CLAMPS — `spanOfWordList()` truncates,
 * because a span running off the end of a shortened ayah is precisely the
 * tier-3 case `classifyDrift` must be able to evaluate, and it needs the
 * truncated span to compare, not an exception. Both behaviours are correct and
 * they are not interchangeable, so they are separate functions here too.
 */

import {
	ANCHOR_SPACE_VERSION,
	QUOTE_MAX_CHARS,
	anchorHashes,
	ayahRef,
	makeQuote,
	rangeRef,
	spanOfWordList,
	spanText,
	wordCountOf,
	wordsRef,
	type Anchor,
	type DriftEvidence
} from '../anchor';
import { ContentError, type AyahModel } from './types';

export { QUOTE_MAX_CHARS, makeQuote };

/**
 * Everything Layer 3 stores for one anchor, ready to spread onto an `Anchor`.
 *
 * `anchor_space_version` is included and not left to the caller: an anchor whose
 * hashes were minted under one segmentation and whose version field says another
 * is worse than one with no hashes at all, because the ladder trusts it.
 */
export interface AnchorIntegrity {
	/** the canonical `ref` — Layer 1, the only durable field. */
	ref: string;
	/** the verbatim canonical span the hashes were taken over. */
	span: string;
	/** `span`, capped at `QUOTE_MAX_CHARS` code points on a word boundary. */
	quote: string;
	/** `sha256(span).slice(0,16)`. */
	text_hash_raw: string;
	/** `sha256(fold(span)).slice(0,16)`. */
	text_hash_fold: string;
	/** `ANCHOR_SPACE_VERSION` at mint time. */
	anchor_space_version: string;
}

/**
 * The verbatim text of every canonical word of an ayah, in order.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE CHOKE POINT. EVERY `spanOf…` IN THIS FILE GOES THROUGH HERE.
 * ═══════════════════════════════════════════════════════════════════════════
 * `wordAnchorIntegrity` used to validate a NUMBER — `end > model.wordCount` —
 * and never the ARRAY the span is actually taken from. Those are not the same
 * check, because `spanOfWordList()` CLAMPS by design: correct when re-deriving
 * a span against a corpus that may have changed, fatal when minting, because a
 * model whose `words` array is short or shifted produces a syntactically valid
 * `ref` over the wrong characters and hashes it into a note forever.
 *
 * Measured, before this guard existed, against the shipped corpus:
 *
 *   {...model(2:255), words: []}            → ref hafs:2:255:8-13, span "",
 *                                             both hashes e3b0c44298fc1c14
 *                                             (sha256 of the empty string)
 *   {...model(2:255), words: words.slice(4)} → ref hafs:2:255:8-13 over words
 *                                             12..16 of Ayat al-Kursi
 *
 * `ayahParts()` accepted both, because it never reads `model.words` — it walks
 * `model.segments`. So this is the sibling seam to the one `parts.ts` guards,
 * and it gets the same treatment, for the same stated reason: a future caller
 * that SYNTHESISES an `AyahModel` — a test double, a search-result preview, a
 * note-editor quote — is the path by which a four-word shift reaches a user.
 *
 * Three counts, not two: the array's own length, the model's claim about it,
 * and the frozen fixture. `buildAyahModel` already makes all three agree, so on
 * a healthy install this costs one array-length comparison and one table lookup
 * per span.
 */
function wordTexts(model: AyahModel): string[] {
	const frozen = wordCountOf(model.ayahId);
	if (model.words.length !== model.wordCount || model.words.length !== frozen) {
		throw new ContentError(
			'word_count_mismatch',
			`ayahId ${model.ayahId} — ${model.surah}:${model.ayah}`,
			`the model carries ${model.words.length} word segments, claims ${model.wordCount} and the ` +
				`frozen fixture says ${frozen}; refusing to take a span from it`
		);
	}
	return model.words.map((word) => word.text);
}

/**
 * The canonical span of words `[start, end)` of one ayah — end EXCLUSIVE,
 * matching the `ref` grammar, and CLAMPED to the words that exist.
 *
 * Use this on the read path and when building drift evidence. To mint a new
 * anchor use `wordAnchorIntegrity`, which refuses an out-of-range span instead
 * of quietly shortening it.
 */
export function spanOfWordRange(model: AyahModel, start: number, end: number): string {
	return spanOfWordList(wordTexts(model), start, end);
}

/** The canonical span of a whole ayah: every canonical word, basmala excluded. */
export function spanOfAyahModel(model: AyahModel): string {
	return spanText(wordTexts(model));
}

/**
 * The canonical span of a run of consecutive ayat, for a `range` anchor.
 * Joined with the same single U+0020 that separates words, so the hash of a
 * range does not depend on how the corpus happened to break lines.
 */
export function spanOfAyahRange(models: readonly AyahModel[]): string {
	return spanText(models.map((model) => spanOfAyahModel(model)));
}

/**
 * The quote and both hashes for an already-canonical span.
 *
 * `span` must be the output of one of the `spanOf…` functions above. Hashing
 * anything else — the raw `t`, a DOM `textContent`, a join with a different
 * separator — produces a hash that will never match on the next read, and a
 * note that reports drift the first time it is opened.
 */
export async function integrityOfSpan(span: string): Promise<Omit<AnchorIntegrity, 'ref'>> {
	const { text_hash_raw, text_hash_fold } = await anchorHashes(span);
	return {
		span,
		quote: makeQuote(span),
		text_hash_raw,
		text_hash_fold,
		anchor_space_version: ANCHOR_SPACE_VERSION
	};
}

/**
 * MINT a word anchor: `hafs:S:A:start-end` plus its span, quote and hashes.
 *
 * Rejects a range that is not entirely inside the ayah. THREE checks and not
 * one, and they are three because each catches something the others cannot:
 *
 *   1. the bounds check below — `[start, end)` is a range at all, and fits
 *      inside `model.wordCount`;
 *   2. `wordsRef()` — the range fits inside the FROZEN FIXTURE's count for this
 *      ayah, which is the count the `ref` grammar is defined against;
 *   3. `wordTexts()`, reached through `spanOfWordRange` — the model's `words`
 *      ARRAY is the right length, so the span is taken from the words the first
 *      two checks were talking about.
 *
 * (3) is the one that used to be missing, and the one that matters: (1) and (2)
 * both validate a number, and a model can carry an honest `wordCount` beside a
 * short or shifted `words` array. See `wordTexts()` for the measured failures.
 */
export async function wordAnchorIntegrity(
	model: AyahModel,
	start: number,
	end: number
): Promise<AnchorIntegrity> {
	if (
		!Number.isInteger(start) ||
		!Number.isInteger(end) ||
		start < 0 ||
		end <= start ||
		end > model.wordCount
	) {
		throw new ContentError(
			'word_span_out_of_range',
			`ayahId ${model.ayahId} — ${model.surah}:${model.ayah}`,
			`[${start}, ${end}) is not a word range inside an ayah of ${model.wordCount} canonical words`
		);
	}
	const ref = wordsRef(model.surah, model.ayah, start, end);
	const span = spanOfWordRange(model, start, end);
	return { ref, ...(await integrityOfSpan(span)) };
}

/** MINT a whole-ayah anchor: `hafs:S:A` plus its span, quote and hashes. */
export async function ayahAnchorIntegrity(model: AyahModel): Promise<AnchorIntegrity> {
	const ref = ayahRef(model.surah, model.ayah);
	const span = spanOfAyahModel(model);
	return { ref, ...(await integrityOfSpan(span)) };
}

/**
 * MINT an ayah-range anchor: `hafs:S:A-S:A` plus its span, quote and hashes.
 * `models` must be the consecutive ayat of the range, in order.
 */
export async function rangeAnchorIntegrity(models: readonly AyahModel[]): Promise<AnchorIntegrity> {
	const first = models[0];
	const last = models[models.length - 1];
	if (!first || !last) {
		throw new ContentError('word_span_out_of_range', 'range', 'a range needs at least one ayah');
	}
	const ref = rangeRef(first.surah, first.ayah, last.surah, last.ayah);
	const span = spanOfAyahRange(models);
	return { ref, ...(await integrityOfSpan(span)) };
}

/**
 * The measured half of `classifyDrift`'s input, for an anchor that lives in one
 * ayah: the span as it is on THIS device today, its two hashes, and the two word
 * counts the tier-3 test compares.
 *
 * `current_word_count` is read off the MODEL (the text installed here) and
 * `fixture_word_count` off the FROZEN FIXTURE (the text the anchor space was
 * frozen against). The ladder's "word count for the ayah changed" row is exactly
 * the comparison of those two, and it can only fire if they come from different
 * places — which is why `wordmap.ts` refuses to regenerate itself at runtime.
 *
 * The caller supplies `from_import`; nothing here can know it.
 */
export async function driftEvidenceOfAyah(
	anchor: Anchor,
	model: AyahModel,
	fromImport: 0 | 1 = 0
): Promise<DriftEvidence> {
	const hasWordSpan = anchor.word_start !== undefined && anchor.word_end !== undefined;
	const span = hasWordSpan
		? spanOfWordRange(model, anchor.word_start as number, anchor.word_end as number)
		: spanOfAyahModel(model);
	const { text_hash_raw, text_hash_fold } = await anchorHashes(span);
	return {
		ayah_id_valid: 1,
		current_hash_raw: text_hash_raw,
		current_hash_fold: text_hash_fold,
		current_word_count: model.wordCount,
		fixture_word_count: wordCountOf(model.ayahId),
		current_span: span,
		from_import: fromImport
	};
}
